import { useCallback } from "react"
import { useConnectorControllerGetAllConnectorsQuery } from "../api/connectorApi"
import {
  useCompanyConnectorControllerCreateCompanyConnectorMutation,
  useCompanyConnectorControllerReAuthCompanyConnectorMutation,
} from "../api/companyConnectorApi"

export default function useConnectors() {
  const {
    data: connectors = [],
    isError: isGetAllConnectorsError,
    isLoading: isGetAllConnectorsLoading,
  } = useConnectorControllerGetAllConnectorsQuery()

  const getConnectorById = useCallback(
    (id: string) => {
      const connector = connectors.find(c => c.id === id)
      return connector
    },
    [connectors],
  )

  const [connectAsync] =
    useCompanyConnectorControllerCreateCompanyConnectorMutation()
  const [reAuthAsync] =
    useCompanyConnectorControllerReAuthCompanyConnectorMutation()

  const connect = async (
    service: string,
    preConfig?: Record<string, unknown>,
    options?: { forceNew?: boolean },
  ) => {
    // Intentionally re-throw the original RTK Query error (not a wrapped generic Error)
    // so callers like AdditionalConfigForm can read error.data.errors / error.data.message
    // and render field-level validation feedback.
    const response = await connectAsync({
      createCompanyConnectorDto: {
        service,
        preConfig,
        forceNew: options?.forceNew,
      },
    }).unwrap()

    return {
      uri: response.uri,
      companyConnectorId: response.companyConnectorId,
      requiresPreConfig: response.requiresPreConfig,
    }
  }

  const reAuth = async (
    companyConnectorId: string,
    preConfig?: Record<string, unknown>,
  ) => {
    // See connect(): re-throw the original error so pre-config validation details
    // surface in the form.
    const response = await reAuthAsync({
      id: companyConnectorId,
      reAuthCompanyConnectorDto: { preConfig },
    }).unwrap()

    return {
      uri: response.uri,
      companyConnectorId: response.companyConnectorId,
      requiresPreConfig: response.requiresPreConfig,
    }
  }

  return {
    connectors,
    isGetAllConnectorsError,
    isGetAllConnectorsLoading,
    connect,
    reAuth,
    getConnectorById,
  }
}
